import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import askAiService from "../services/askAiService";
import { useAuth } from "../hooks/useAuth";

const AskAiContext = createContext();

// The send endpoint returns as soon as the message rows exist — the
// actual plan/narrate work runs on Celery, not inline in the request — so
// a pending assistant message is polled until it resolves. Interval is
// short because it's just a cheap DB read each time, not a provider round
// trip.
//
// The overall budget comes from /askai/config/ (poll_timeout_ms), derived
// there from ASKAI_TIMEOUT and the provider's retry count. It used to be
// hardcoded at 90s while the backend worst case was already ~120s, so a
// slow answer showed "Ask AI timed out" to the user while the task went
// on to succeed and write an answer they never saw. The constant below is
// only the fallback for when config has not loaded yet.
const POLL_INTERVAL_MS = 1200;
const DEFAULT_POLL_TIMEOUT_MS = 140000;
const DEFAULT_MAX_MESSAGE_CHARS = 4000;

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Swap an updated message into the thread in place, keeping its position.
// A resolved answer replaces the pending bubble it started as, rather than
// being appended after it.
const _replaceMessage = (messages, updated) =>
  messages.map((m) => (m.message_id === updated.message_id ? updated : m));

/**
 * AskAiProvider
 *
 * Purpose: Owns the single Ask AI drawer's open/closed state and
 * conversation/message data so the trigger button (mounted per-page inside
 * PageHeader) and the drawer itself (mounted once in App.js) share state
 * without prop drilling through AppShell, which remounts on every route
 * change. Current-page context is held in a ref (set imperatively by
 * useRegisterPageContext) rather than state, so registering it doesn't
 * re-render every page that calls the hook.
 */
export function AskAiProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    role_enabled: false,
    max_message_chars: DEFAULT_MAX_MESSAGE_CHARS,
    poll_timeout_ms: DEFAULT_POLL_TIMEOUT_MS,
    loaded: false,
  });
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const pageContextRef = useRef(null);
  // Mirrors activeConversationId so the async send/poll flow can check
  // which thread is on screen *now*, rather than the one captured in its
  // closure when the question was asked. See sendMessage.
  const activeConversationIdRef = useRef(null);
  // selectConversation is declared before resumePending and both are
  // memoised; a ref keeps the dependency one-directional instead of
  // reordering the file around a callback cycle.
  const resumePendingRef = useRef(() => {});

  // An older backend won't send the limits, so each falls back to its
  // default rather than becoming undefined and disabling the composer.
  const loadConfig = useCallback(async () => {
    try {
      const data = await askAiService.getConfig();
      setConfig({
        enabled: !!data.enabled,
        role_enabled: !!data.role_enabled,
        max_message_chars: data.max_message_chars || DEFAULT_MAX_MESSAGE_CHARS,
        poll_timeout_ms: data.poll_timeout_ms || DEFAULT_POLL_TIMEOUT_MS,
        loaded: true,
      });
    } catch {
      setConfig({
        enabled: false,
        role_enabled: false,
        max_message_chars: DEFAULT_MAX_MESSAGE_CHARS,
        poll_timeout_ms: DEFAULT_POLL_TIMEOUT_MS,
        loaded: true,
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadConfig();
    }
  }, [isAuthenticated, loadConfig]);

  const setPageContext = useCallback((context) => {
    pageContextRef.current = context;
  }, []);

  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const data = await askAiService.listConversations();
      setConversations(data.results || []);
      return data.results || [];
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load conversations.");
      return [];
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    setLoadingMessages(true);
    try {
      const data = await askAiService.listMessages(conversationId);
      const loaded = data.results || [];
      setMessages(loaded);
      return loaded;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load messages.");
      setMessages([]);
      return [];
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const selectConversation = useCallback(
    async (conversationId) => {
      // Ref is set synchronously alongside the state: an in-flight send
      // must see the switch immediately, before React has re-rendered.
      activeConversationIdRef.current = conversationId;
      setActiveConversationId(conversationId);
      setError(null);
      const loaded = await fetchMessages(conversationId);
      // An answer may still be in flight from a previous visit — pick the
      // wait back up rather than leaving a bubble stuck on "Thinking…".
      resumePendingRef.current(conversationId, loaded);
    },
    [fetchMessages],
  );

  const startNewConversation = useCallback(async () => {
    setError(null);
    const conversation = await askAiService.createConversation();
    setConversations((current) => [conversation, ...current]);
    activeConversationIdRef.current = conversation.conversation_id;
    setActiveConversationId(conversation.conversation_id);
    setMessages([]);
    return conversation;
  }, []);

  // Polls MessageDetailView until the assistant message leaves "pending"
  // (task finished — complete, degraded, or failed) or POLL_TIMEOUT_MS
  // elapses. A poll request itself failing (transient network blip) is
  // tolerated and simply retried on the next tick rather than aborting the
  // whole wait — only running out of time gives up.
  const pollForCompletion = useCallback(
    async (conversationId, messageId, timeoutMs, onProgress) => {
      const deadline = Date.now() + (timeoutMs || DEFAULT_POLL_TIMEOUT_MS);
      for (;;) {
        if (Date.now() >= deadline) {
          throw new Error("Ask AI timed out waiting for an answer.");
        }
        await wait(POLL_INTERVAL_MS);
        let message;
        try {
          message = await askAiService.getMessage(conversationId, messageId);
        } catch {
          continue;
        }
        if (message.status !== "pending") {
          return message;
        }
        // The task writes the answer to the row as it is generated, so a
        // still-pending poll often carries real text. Showing it is what
        // makes a slow answer feel like it is happening rather than hung
        // — no SSE or websocket involved, just the poll that already runs.
        if (message.content) {
          onProgress?.(message);
        }
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (content) => {
      let conversationId = activeConversationId;
      if (!conversationId) {
        const conversation = await startNewConversation();
        conversationId = conversation.conversation_id;
      }
      setSending(true);
      setError(null);
      // An answer can take a couple of minutes, and the user is free to
      // switch threads while it runs. Every setMessages below is guarded
      // on this conversation still being the one on screen — without it,
      // thread A's answer appends into whichever thread B is now open.
      const isStillActive = () => activeConversationIdRef.current === conversationId;
      try {
        const data = await askAiService.sendMessage(conversationId, content, pageContextRef.current);
        // Both bubbles render immediately — the question, and the pending
        // answer. The pending one used to be left out of `messages`
        // entirely, so a wait that timed out ended with an error toast and
        // no bubble at all; now the answer has a place in the thread from
        // the start and simply fills in.
        if (isStillActive()) {
          setMessages((current) => [...current, data.user_message, data.assistant_message]);
        }

        let assistantMessage = data.assistant_message;
        if (assistantMessage.status === "pending") {
          assistantMessage = await pollForCompletion(
            conversationId,
            assistantMessage.message_id,
            config.poll_timeout_ms,
            (partial) => {
              if (isStillActive()) {
                setMessages((current) => _replaceMessage(current, partial));
              }
            },
          );
        }

        if (isStillActive()) {
          setMessages((current) => _replaceMessage(current, assistantMessage));
        }
        // The server titles a new conversation from its first message
        // (_title_from_content), so take the title from the response
        // rather than mapping it onto itself, which left new threads
        // showing a blank title until the next refetch.
        if (data.conversation_title) {
          setConversations((current) =>
            current.map((c) =>
              c.conversation_id === conversationId ? { ...c, title: data.conversation_title } : c,
            ),
          );
        }
        return { ...data, assistant_message: assistantMessage };
      } catch (err) {
        if (isStillActive()) {
          setError(err.response?.data?.detail || "Ask AI could not answer right now. Please try again.");
        }
        throw err;
      } finally {
        setSending(false);
      }
    },
    [activeConversationId, startNewConversation, pollForCompletion, config.poll_timeout_ms],
  );

  // Picking a thread back up mid-answer. The poll lives inside the
  // sendMessage call, so closing the drawer, navigating or refreshing
  // abandoned it — the answer landed in the database and the UI only ever
  // showed it on some later visit. Loading a conversation that still has a
  // pending message now resumes waiting for it.
  const resumePending = useCallback(
    async (conversationId, loaded) => {
      const pending = loaded.find(
        (m) => m.role === "assistant" && m.status === "pending",
      );
      if (!pending) return;

      setSending(true);
      try {
        const resolved = await pollForCompletion(
          conversationId, pending.message_id, config.poll_timeout_ms,
          (partial) => {
            if (activeConversationIdRef.current === conversationId) {
              setMessages((current) => _replaceMessage(current, partial));
            }
          },
        );
        if (activeConversationIdRef.current === conversationId) {
          setMessages((current) => _replaceMessage(current, resolved));
        }
      } catch {
        // Giving up on the wait is not an error worth interrupting the
        // user over — the bubble stays pending and reopening tries again.
      } finally {
        setSending(false);
      }
    },
    [pollForCompletion, config.poll_timeout_ms],
  );

  // Re-runs one failed answer in place. The old retry resent the question
  // as a brand new message, leaving the failed bubble, the original
  // question and a duplicate of it stacked in the thread.
  const retryMessage = useCallback(
    async (messageId) => {
      const conversationId = activeConversationIdRef.current;
      if (!conversationId) return;

      setSending(true);
      setError(null);
      try {
        const data = await askAiService.retryMessage(conversationId, messageId);
        if (activeConversationIdRef.current === conversationId) {
          setMessages((current) => _replaceMessage(current, data.assistant_message));
        }

        const resolved = await pollForCompletion(
          conversationId, messageId, config.poll_timeout_ms,
          (partial) => {
            if (activeConversationIdRef.current === conversationId) {
              setMessages((current) => _replaceMessage(current, partial));
            }
          },
        );
        if (activeConversationIdRef.current === conversationId) {
          setMessages((current) => _replaceMessage(current, resolved));
        }
        return resolved;
      } catch (err) {
        if (activeConversationIdRef.current === conversationId) {
          setError(err.response?.data?.detail || "Ask AI could not answer right now. Please try again.");
        }
        throw err;
      } finally {
        setSending(false);
      }
    },
    [pollForCompletion, config.poll_timeout_ms],
  );

  useEffect(() => {
    resumePendingRef.current = resumePending;
  }, [resumePending]);

  // Stops waiting for an answer. The pending message becomes
  // `cancelled`, which ends the poll naturally — it only loops while the
  // status is "pending".
  const cancelMessage = useCallback(async (messageId) => {
    const conversationId = activeConversationIdRef.current;
    if (!conversationId) return;
    try {
      const data = await askAiService.cancelMessage(conversationId, messageId);
      if (activeConversationIdRef.current === conversationId) {
        setMessages((current) => _replaceMessage(current, data.assistant_message));
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not stop that answer.");
    }
  }, []);

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);
  const toggleDrawer = useCallback(() => setIsOpen((current) => !current), []);

  const value = useMemo(
    () => ({
      isOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      config,
      conversations,
      activeConversationId,
      messages,
      loadingConversations,
      loadingMessages,
      sending,
      error,
      setError,
      setPageContext,
      fetchConversations,
      selectConversation,
      startNewConversation,
      sendMessage,
      retryMessage,
      cancelMessage,
    }),
    [
      isOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      config,
      conversations,
      activeConversationId,
      messages,
      loadingConversations,
      loadingMessages,
      sending,
      error,
      setPageContext,
      fetchConversations,
      selectConversation,
      startNewConversation,
      sendMessage,
      retryMessage,
      cancelMessage,
    ],
  );

  return <AskAiContext.Provider value={value}>{children}</AskAiContext.Provider>;
}

AskAiProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAskAiContext = () => {
  const context = useContext(AskAiContext);
  if (!context) {
    throw new Error("useAskAiContext must be used within AskAiProvider");
  }
  return context;
};

export default AskAiContext;
